import type { ItemCategory } from './types/item';

// 单调递增计数器，防止同毫秒生成相同 ID
let ephemeralCounter = 0;

function toSafeItemIdSegment(value: string, fallback: string): string {
  const normalized = value
    .trim()
    .toLowerCase()
    .replace(/[^a-z0-9_]+/g, '_')
    .replace(/^_+|_+$/g, '');
  return normalized || fallback;
}

function hashStableSeed(seed: string): string {
  let hash = 2166136261;
  for (let index = 0; index < seed.length; index += 1) {
    hash ^= seed.charCodeAt(index);
    hash = Math.imul(hash, 16777619);
  }
  return (hash >>> 0).toString(36);
}

/**
 * 生成内建物品模板 ID。
 */
export function buildBuiltinItemTemplateId(category: ItemCategory, semanticName: string): string {
  const normalizedCategory = toSafeItemIdSegment(category, 'special');
  const normalizedName = toSafeItemIdSegment(semanticName, 'unnamed_item');
  return `item_${normalizedCategory}_${normalizedName}`;
}

/**
 * 生成稳定的运行时物品模板 ID。
 */
export function buildStableGeneratedItemTemplateId(
  category: ItemCategory,
  semanticName: string,
  contextParts: Array<string | null | undefined> = [],
): string {
  const normalizedCategory = toSafeItemIdSegment(category, 'special');
  const normalizedName = toSafeItemIdSegment(semanticName, 'unknown_item');
  const normalizedContextParts = contextParts.map(part => toSafeItemIdSegment(part ?? '', '')).filter(Boolean);
  const seed = [normalizedCategory, normalizedName, ...normalizedContextParts].join('||');
  return `item_${normalizedCategory}_${normalizedName}_${hashStableSeed(seed || 'unknown_item')}`;
}

/**
 * 生成一次性运行时物品模板 ID。
 */
export function buildEphemeralItemTemplateId(category: ItemCategory, semanticName: string): string {
  const normalizedCategory = toSafeItemIdSegment(category, 'special');
  const normalizedName = toSafeItemIdSegment(semanticName, 'unknown_item');
  return `item_${normalizedCategory}_${normalizedName}_${Date.now()}_${++ephemeralCounter}`;
}

/**
 * 内建物品模板 ID 常量。
 */
export const BUILTIN_ITEM_TEMPLATE_IDS = {
  RUSTY_SWORD: buildBuiltinItemTemplateId('equipment', 'rusty_sword'),
  TRAVELER_CLOTH: buildBuiltinItemTemplateId('equipment', 'traveler_cloth'),
  COPPER_RING: buildBuiltinItemTemplateId('equipment', 'copper_ring'),
  FORTUNE_CHARM: buildBuiltinItemTemplateId('equipment', 'fortune_charm'),
  HP_POTION_SMALL: buildBuiltinItemTemplateId('consumable', 'hp_potion_small'),
  COMMON_HERB: buildBuiltinItemTemplateId('material', 'common_herb'),
} as const;
